// React specific
import React from 'react';
import { useIntl } from 'react-intl';
import { differenceInCalendarDays } from 'date-fns';

// Components
import Card from '../../../components/Card';
import Button from '../../../components/Button';
import { useModal } from '../../../components/Modals/ModalContext';

// Translations
import claimDetailsMessages from './claimDetailsMessages';
import claimPassengerListMessages from './ClaimPassengerList/claimPassengerListMessages';
import httpErrorMessages from '../../../messages/httpErrorMessages';
import translations from '../../../translations/main.json';

// Utils
import { useTranslatedNavigate } from '../../../utils/routes';

/** Length of the cooling-off period, in days. */
const COOLING_OFF_DAYS = 15;

/** "Cancel claim" support topic, pre-selected on the contact-us page. */
const CANCEL_CLAIM_TOPIC_ID = '16';

interface CloseClaimProps {
  /** ISO 8601 date string, e.g. "2024-04-03T10:07:08+02:00". */
  createdAt?: string;
}

/**
 * "Close claim" card shown on the claim details page.
 *
 * Visibility is driven by the claim's `created_at`: the card is only shown
 * while the claim is still within the cooling-off window, i.e. it was created
 * less than COOLING_OFF_DAYS days ago.
 */
const CloseClaim = ({ createdAt }: CloseClaimProps): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const [showModal] = useModal('default');
  const translatedNavigate = useTranslatedNavigate();

  if (!createdAt) {
    return null;
  }

  const ageInDays = differenceInCalendarDays(new Date(), new Date(createdAt));
  // Show only while the claim is younger than the cooling-off period.
  const showCloseClaim = ageInDays < COOLING_OFF_DAYS;

  if (!showCloseClaim) {
    return null;
  }

  function handleCloseClaimClick() {
    showModal({
      title: formatMessage(claimDetailsMessages.closeClaimTitle),
      body: (
        <p className={'text-center font-open-sans text-gray-400 m-0'}>
          {formatMessage(claimDetailsMessages.closeClaimModalBody)}
        </p>
      ),
      onCancelText: formatMessage(claimPassengerListMessages.cancelButton),
      onConfirmText: formatMessage(httpErrorMessages.errorModalCloseText),
      onConfirm: () => {
        // Redirect to the contact-us page with the "Cancel claim" support
        // topic pre-selected (read from navigation state on that page).
        translatedNavigate(
          [
            '/',
            translations['cc4.seo.route.account'],
            translations['cc4.account-page.help.message-contact-us-route'],
          ],
          { state: { selectedTopicId: CANCEL_CLAIM_TOPIC_ID } }
        );
      },
    });
  }

  return (
    <Card
      className={'mt-30 font-open-sans'}
      style={{
        paddingTop: 20,
        paddingBottom: 20,
        paddingLeft: 30,
        paddingRight: 30,
      }}
    >
      <div
        className={
          'flex w-full flex-col sm:flex-row sm:items-center justify-between'
        }
      >
        <div className={'flex-1'}>
          <p className={'font-pt-sans font-bold text-lg text-gray-250 m-0 p-0'}>
            {formatMessage(claimDetailsMessages.closeClaimTitle)}
          </p>
          <p className={'text-base text-gray-400 m-0 mt-5'}>
            {formatMessage(claimDetailsMessages.closeClaimDescription)}
          </p>
        </div>
        <Button
          theme={'secondary'}
          showIcon={false}
          width={'auto'}
          className={'mt-15 sm:mt-0 sm:ml-30'}
          onClick={handleCloseClaimClick}
        >
          {formatMessage(claimDetailsMessages.closeClaimTitle)}
        </Button>
      </div>
    </Card>
  );
};

export default CloseClaim;
